import { post } from '@/api/client';
import { forgetDestination } from '@/auth/destination';
import { type SessionUser, useAuth } from '@/auth/store';
import { T } from '@/i18n';
/**
 * /demo?token=: where the demo link from the email lands (owner, 2026-09-27: the demo with only an
 * address). Opening the page does not consume the link: a person must press the button, so passive email
 * previews do not sign in. The button confirms the address and opens the person's own demo, made on the first visit,
 * and the app starts in it. A used or expired link offers a new one.
 */
import { track } from '@/marketing/analytics';
import { countStep } from '@/marketing/count';
import { commitGeneration } from '@/shell/context';
import { Button } from '@/ui';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BotCheck,
  KontoBox,
  demoHome,
  failureCode,
  failureText,
  hint,
  seedFromQuery,
  tokenFromQuery,
} from './shared';

interface Opened {
  first_confirmation?: boolean;
  new_demo?: boolean;
  demo?: 'bygg' | 'cafe';
  token: string | null;
  role: string | null;
  user: SessionUser;
}

export default function DemoLenke() {
  const nav = useNavigate();
  const { search } = useLocation();
  const setSession = useAuth((s) => s.setSession);
  const [err, setErr] = useState<{ text: string; again: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const started = useRef(false); // Synchronous guard against two clicks before React renders.
  const token = tokenFromQuery(search);
  // the bot check runs while the page is read; the button waits a moment for it, then lets the server decide
  const [check, setCheck] = useState(0);
  const [botErr, setBotErr] = useState<string | null>(null);
  const botRef = useRef<string | null>(null);
  const waitForCheck = async () => {
    if (!(window as unknown as { turnstile?: unknown }).turnstile) return botRef.current;
    for (let i = 0; i < 25 && !botRef.current; i++) await new Promise((ok) => setTimeout(ok, 200));
    return botRef.current;
  };
  // counted for every visitor, no cookie and nothing about the person (marketing/count.ts)
  useEffect(() => countStep('link_page'), []);
  // from "Fortsett med Google/Microsoft" (2026-09-30): the person just signed in, so no button to
  // press; the button stays for a mail link, which an inbox preview may open without them
  const via = new URLSearchParams(search).get('via');
  const problem = err ?? (!token ? { text: T('Lenken er ikke gyldig.'), again: true } : null);

  async function openDemo() {
    if (!token || started.current) return;
    started.current = true;
    setBusy(true);
    // back from a sign-in the server needs no bot check (its link is demo_signin), so no wait for one
    const turnstile_token = via ? null : await waitForCheck();
    post<Opened>('/account/demo-link/open', { token, turnstile_token, ...seedFromQuery(search) })
      .then((r) => {
        if (!r.token)
          return setErr({
            text: T('Demoen ble laget, men kunne ikke åpnes. Prøv igjen.'),
            again: true,
          });
        setSession(r.token, { ...r.user, role: r.role });
        commitGeneration(r.user.id, null, null); // the token names the demo; nothing from before is readable
        forgetDestination();
        // a new café demo starts on Vaktplan, where Hugin's opening question is about; a new trade firm on Timer
        // (planning off, #128); a demo already seen starts on Hjem
        nav(demoHome(r), { replace: true });
      })
      .catch((ex) => {
        const code = failureCode(ex);
        track('onboarding_failed', {
          stage: 'demo_link',
          error_code: ['invalid', 'expired', 'used', 'bot_check'].includes(code ?? '') ? code : 'other',
        });
        if (code === 'bot_check') {
          // the link is not used up: a fresh check, and the same button again
          started.current = false;
          botRef.current = null;
          setCheck((n) => n + 1);
          setBotErr(failureText(ex));
          return;
        }
        // demo_cap (issue #16): the link is used up, so tomorrow they need a new one
        setErr({
          text: failureText(ex),
          again: true,
        });
      })
      .finally(() => setBusy(false));
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: once, on arrival (started guards a second call)
  useEffect(() => {
    if (via === 'google' || via === 'microsoft' || via === 'apple') openDemo();
  }, []);

  return (
    <KontoBox title={T('Din demobedrift')}>
      <div className="opt" style={{ display: 'grid', gap: 10 }}>
        {problem ? (
          <>
            <div className="al dgr" style={{ margin: 0 }}>
              {problem.text}
            </div>
            {problem.again ? (
              <Link to="/registrer?next=demo" style={hint}>
                {T('Få en ny lenke')}
              </Link>
            ) : null}
          </>
        ) : (
          <>
            <p style={hint}>{T('Trykk nedenfor for å bekrefte e-postadressen og åpne demobedriften din.')}</p>
            {botErr && (
              <div className="al dgr" style={{ margin: 0 }}>
                {botErr}
              </div>
            )}
            <BotCheck
              key={check}
              onToken={(t) => {
                botRef.current = t;
              }}
            />
            <Button tone="pri" type="button" onClick={openDemo} disabled={busy}>
              {busy ? T('Åpner demoen …') : T('Åpne demoen')}
            </Button>
          </>
        )}
      </div>
    </KontoBox>
  );
}
